// Central source of truth for how positions are labeled and grouped in the
// UI. Keep this in sync with models/Advert.js POSITIONS on the backend.
//
// `size` is the standard upload size (2x the on-screen size at a 1440 px
// window). Same table as tramango-core ADS_DOCUMENTATION.md ("Position sizes")
// and tramango-webapp AD_SIZES — change all three together. Positions without
// one fall back to the generic hero/standard recommendation in the ad form.

export const POSITION_GROUPS = [
  {
    group: "Flight",
    positions: [
      { value: "FLIGHT_HOME", label: "Flight Home", size: "2560 × 600" },
      { value: "FLIGHT_RESULTS", label: "Flight Results", size: "492 × 616" },
      {
        value: "FLIGHT_RESULTS_IN_LIST",
        label: "Flight Results: In list",
        size: "1536 × 256",
      },
      {
        value: "FLIGHT_PASSENGER_FORM",
        label: "Passenger Info Form",
        size: "780 × 976",
      },
      { value: "FLIGHT_ADDONS", label: "Flight Add-ons" },
      { value: "FLIGHT_PAYMENT", label: "Flight Payment", size: "780 × 976" },
    ],
  },
  {
    group: "Event",
    positions: [
      { value: "EVENT_RESULTS", label: "Event Results" },
      { value: "EVENT_DETAILS", label: "Event Details" },
      { value: "EVENT_REGISTRATION", label: "Event Registration" },
      { value: "EVENT_ADDONS", label: "Event Add-ons" },
      { value: "EVENT_PAYMENT", label: "Event Payment" },
    ],
  },
  {
    group: "Account",
    positions: [
      {
        value: "ACCOUNT_TRAVEL_HUB",
        label: "Account: Travel Hub",
        size: "1136 × 624",
      },
      {
        value: "ACCOUNT_MY_TRIPS",
        label: "Account: My Trips",
        size: "1136 × 624",
      },
    ],
  },
  {
    group: "Insurance",
    positions: [
      {
        // Two half-width slots under the quote form; one ad per slot
        value: "INSURANCE_PROMO",
        label: "Insurance: Hero images",
        size: "1260 × 560",
      },
    ],
  },
  {
    group: "Other",
    positions: [
      { value: "AUTHENTICATION", label: "Authentication" },
      { value: "CAMPAIGNS", label: "Campaigns", size: "2560 × 600" },
    ],
  },
];

export const ALL_POSITION_OPTIONS = POSITION_GROUPS.flatMap((g) => g.positions);

export const positionMeta = (value) =>
  ALL_POSITION_OPTIONS.find((p) => p.value === value);

export const positionLabel = (value) => {
  if (value === "*") return "All Positions";
  const found = ALL_POSITION_OPTIONS.find((p) => p.value === value);
  return found ? found.label : value;
};

// The summary endpoint groups wildcard ("*") ads under their own "*"
// bucket rather than folding them into every position's totals (unlike
// findServable, which does fold them in). This merges the "*" bucket's
// numbers into every specific position so counts shown in the UI reflect
// what would actually serve there. Ideally getAdvertsSummary is fixed to
// do this server-side later — this is a client-side stopgap.
export const mergeWildcardIntoSummary = (summary = []) => {
  const wildcard = summary.find((s) => s.position === "*");
  const byPosition = new Map(summary.map((s) => [s.position, { ...s }]));

  ALL_POSITION_OPTIONS.forEach(({ value }) => {
    const existing = byPosition.get(value) || {
      position: value,
      totalAds: 0,
      totalImpressions: 0,
      totalClicks: 0,
      ctr: 0,
    };
    if (wildcard) {
      existing.totalAds += wildcard.totalAds;
      existing.totalImpressions += wildcard.totalImpressions;
      existing.totalClicks += wildcard.totalClicks;
    }
    byPosition.set(value, existing);
  });

  return byPosition;
};
